import { FunnelIcon } from "@phosphor-icons/react/dist/csr/Funnel";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { Text } from "@volca/ui/components/ui/text";
import { Toggle } from "@volca/ui/components/ui/toggle";
import { cn } from "@volca/ui/lib/utils";
import { dayjs } from "@/core/utils/date";
import type { PayrollRosterRowFocus } from "./types";

interface RosterMemberSummaryProps {
  memberCount: number;
  displayedCount: number;
  filteredCount: number;
  suggestionCount: number;
  departedCount: number;
  focus: PayrollRosterRowFocus;
  hasActiveFilters: boolean;
  publishedAt: string | null;
  onFocusChange: (focus: Exclude<PayrollRosterRowFocus, null>) => void;
}

interface AttentionFilterProps {
  active: boolean;
  count: number;
  label: string;
  tone: "negative" | "warning";
  onClick: () => void;
}

const AttentionFilter = ({
  active,
  count,
  label,
  tone,
  onClick,
}: AttentionFilterProps) => (
  <Toggle
    size="sm"
    variant={active ? "outline" : "default"}
    pressed={active}
    onPressedChange={onClick}
    aria-label={active ? `Clear ${label} filter` : `${count} ${label}`}
    className={cn("align-middle", {
      "rounded-full": active,
      "underline decoration-dotted underline-offset-2 hover:decoration-solid":
        !active,
      "border-danger-line text-danger aria-pressed:bg-danger-subtle":
        active && tone === "negative",
      "border-warning-line text-warning aria-pressed:bg-warning-subtle":
        active && tone === "warning",
      "text-danger": !active && tone === "negative",
      "text-warning": !active && tone === "warning",
    })}
  >
    {active ? (
      <>
        <FunnelIcon />
        <span>
          Filter: {label} ({count})
        </span>
        <XIcon />
      </>
    ) : (
      `${count} ${label}`
    )}
  </Toggle>
);

export const RosterMemberSummary = ({
  memberCount,
  displayedCount,
  filteredCount,
  suggestionCount,
  departedCount,
  focus,
  hasActiveFilters,
  publishedAt,
  onFocusChange,
}: RosterMemberSummaryProps) => (
  <Text variant="caption" tone="muted">
    {hasActiveFilters
      ? `${filteredCount} of ${displayedCount} people`
      : `${memberCount} ${memberCount === 1 ? "person" : "people"}`}
    {(suggestionCount > 0 || focus === "missing") && (
      <>
        {" · "}
        <AttentionFilter
          active={focus === "missing"}
          count={suggestionCount}
          label="missing from roster"
          tone="negative"
          onClick={() => onFocusChange("missing")}
        />
      </>
    )}
    {(departedCount > 0 || focus === "departed") && (
      <>
        {" · "}
        <AttentionFilter
          active={focus === "departed"}
          count={departedCount}
          label="not in ServiceTitan"
          tone="warning"
          onClick={() => onFocusChange("departed")}
        />
      </>
    )}
    {publishedAt
      ? ` · last published ${dayjs(publishedAt).format("MMM D, YYYY")}`
      : ""}
  </Text>
);
